import { useEffect, useRef, useState } from 'react';

import { Search } from 'lucide-react';

import useDebounced from '../../../hooks/useDebounced';

interface OptionItem {
  id: number;
  name: string;
}

interface POSSearchFieldProps {
  options: OptionItem[];

  selectedValue: number | null;

  onSelect: (
    selected: OptionItem | null
  ) => void;

  onSearch?: (
    searchTerm: string
  ) => void;

  placeholder?: string;

  disabled?: boolean;

  error?: string;
}

const POSSearchField: React.FC<
  POSSearchFieldProps
> = ({
  options,

  selectedValue,

  onSelect,

  onSearch,

  placeholder = 'Buscar...',

  disabled = false,

  error,
}) => {
  // =====================================================
  // 🔹 State
  // =====================================================
  const [searchTerm, setSearchTerm] =
    useState('');

  const [isOpen, setIsOpen] =
    useState(false);

  const [
    filteredOptions,
    setFilteredOptions,
  ] = useState<OptionItem[]>(options);

  const debouncedSearchTerm =
    useDebounced(searchTerm, 300);

  const containerRef =
    useRef<HTMLDivElement>(null);

  /**
   * ✅ Evita loops infinitos
   */
  const lastSearchTermRef =
    useRef('');

  // =====================================================
  // 🔹 Selected Option
  // =====================================================
  const selectedOption =
    options.find(
      (opt) =>
        opt.id === selectedValue
    ) || null;

  // =====================================================
  // 🔹 Search Logic
  // =====================================================
  useEffect(() => {
    const trimmed =
      debouncedSearchTerm.trim();

    /**
     * ===================================================
     * 🔹 Remote Search
     * ===================================================
     */
    if (onSearch) {
      /**
       * ✅ Evita búsquedas repetidas
       */
      if (
        trimmed !== '' &&
        trimmed !==
          lastSearchTermRef.current
      ) {
        lastSearchTermRef.current =
          trimmed;

        onSearch(trimmed);
      }

      /**
       * ✅ Limpieza
       */
      if (trimmed === '') {
        lastSearchTermRef.current =
          '';

        setFilteredOptions([]);
      }
    }

    /**
     * ===================================================
     * 🔹 Local Search
     * ===================================================
     */
    else {
      if (trimmed === '') {
        setFilteredOptions(options);
      } else {
        setFilteredOptions(
          options.filter((option) =>
            option.name
              .toLowerCase()
              .includes(
                trimmed.toLowerCase()
              )
          )
        );
      }
    }
  }, [
    debouncedSearchTerm,
    options,
    onSearch,
  ]);

  // =====================================================
  // 🔹 Sync Options
  // =====================================================
  useEffect(() => {
    const trimmed =
      debouncedSearchTerm.trim();

    /**
     * ===================================================
     * 🔹 Local Search
     * ===================================================
     */
    if (!onSearch) {
      if (trimmed === '') {
        setFilteredOptions(options);
      } else {
        setFilteredOptions(
          options.filter((option) =>
            option.name
              .toLowerCase()
              .includes(
                trimmed.toLowerCase()
              )
          )
        );
      }
    }

    /**
     * ===================================================
     * 🔹 Remote Search
     * ===================================================
     */
    else {
      setFilteredOptions(options);
    }
  }, [
    options,
    debouncedSearchTerm,
    onSearch,
  ]);

  // =====================================================
  // 🔹 Outside Click
  // =====================================================
  useEffect(() => {
    const handleClickOutside = (
      event: MouseEvent
    ) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(
          event.target as Node
        )
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () =>
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
  }, []);

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div
      ref={containerRef}
      className="relative"
    >
      {/* ===================================================== */}
      {/* 🔹 Input */}
      {/* ===================================================== */}
      <div
        onClick={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        className={`
          flex
          h-[44px]
          items-center
          gap-3

          rounded-xl

          border

          px-4

          transition-all

          ${
            error
              ? 'border-red-500'
              : `
                border-stroke
                hover:border-primary
              `
          }

          bg-white

          dark:border-strokedark
          dark:bg-boxdark
        `}
      >
        {/* 🔹 Icon */}
        <Search
          className="
            h-4
            w-4
            shrink-0

            text-slate-400
          "
        />

        {/* 🔹 Input */}
        <input
          type="text"
          value={
            isOpen
              ? searchTerm
              : selectedOption?.name || ''
          }
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setIsOpen(true)}
          onChange={(e) =>
            setSearchTerm(e.target.value)
          }
          className="
            w-full

            bg-transparent

            text-sm

            outline-none

            dark:text-white
          "
        />
      </div>

      {/* ===================================================== */}
      {/* 🔹 Dropdown */}
      {/* ===================================================== */}
      {isOpen && (
        <div
          className="
            absolute
            z-50
            mt-2
            w-full

            overflow-hidden

            rounded-2xl

            border
            border-stroke

            bg-white

            shadow-lg

            dark:border-strokedark
            dark:bg-boxdark
          "
        >
          <ul
            className="
              max-h-[260px]
              overflow-y-auto
            "
          >
            {filteredOptions.length >
            0 ? (
              filteredOptions.map(
                (option) => {
                  const isSelected =
                    option.id ===
                    selectedValue;

                  return (
                    <li
                      key={option.id}
                      onClick={() => {
                        onSelect(option);

                        /**
                         * ✅ Reset search
                         */
                        setSearchTerm('');

                        /**
                         * ✅ Close
                         */
                        setIsOpen(false);
                      }}
                      className={`
                        cursor-pointer

                        px-4
                        py-3

                        text-sm

                        transition-all

                        ${
                          isSelected
                            ? `
                              bg-primary
                              text-white
                            `
                            : `
                              hover:bg-slate-100

                              dark:hover:bg-meta-4
                            `
                        }
                      `}
                    >
                      {option.name}
                    </li>
                  );
                }
              )
            ) : (
              <li
                className="
                  px-4
                  py-3

                  text-sm
                  text-slate-400
                "
              >
                No se encontraron resultados
              </li>
            )}
          </ul>
        </div>
      )}

      {/* ===================================================== */}
      {/* 🔹 Error */}
      {/* ===================================================== */}
      {error && (
        <span
          className="
            mt-1
            block

            text-xs
            text-red-500
          "
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default POSSearchField;